"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { LocalMp3Track } from "@/hooks/useLocalMp3Player";
import { useSensorStream } from "@/hooks/useSensorStream";
import { createGsrSignal } from "@/lib/gsr";
import {
  createLocalMp3TestSession,
  LOCAL_MP3_TEST_SESSION_MAX_SAMPLES,
  saveLocalMp3TestSession,
  type LocalMp3TestSession,
} from "@/lib/localMp3TestSession";
import { parseSensorPacket } from "@/lib/sensorPacket";
import type { DataBaseline } from "@/store/slices/session.slice";
import type { ParticipantSurveyDraft } from "@/store/slices/participantFlow.slice";

const STORAGE_FLUSH_INTERVAL_MS = 2_000;
const SAMPLE_TRIM_COUNT = 1_000;

type PlaybackContext = {
  currentTrack: LocalMp3Track | null;
  positionMs: number;
  isPlaying: boolean;
};

type UseLocalMp3TestSessionArgs = PlaybackContext & {
  tracks: LocalMp3Track[];
  baseline: DataBaseline | null;
  preSurvey: ParticipantSurveyDraft;
};

export function useLocalMp3TestSession({
  baseline,
  preSurvey,
  tracks,
  currentTrack,
  positionMs,
  isPlaying,
}: UseLocalMp3TestSessionArgs) {
  const sessionRef = useRef<LocalMp3TestSession | null>(null);
  const playbackContextRef = useRef<PlaybackContext>({
    currentTrack,
    positionMs,
    isPlaying,
  });
  const lastStorageFlushAtRef = useRef(0);
  const [storageError, setStorageError] = useState<string | null>(null);

  const persistSession = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;

    const error = saveLocalMp3TestSession(session);
    lastStorageFlushAtRef.current = Date.now();
    setStorageError(error);
  }, []);

  useEffect(() => {
    playbackContextRef.current = {
      currentTrack,
      positionMs,
      isPlaying,
    };

    const session = sessionRef.current;
    if (!session || !currentTrack) return;

    const existingTrack = session.tracks.find(
      (track) => track.id === currentTrack.id,
    );
    const trackSnapshot = {
      id: currentTrack.id,
      fileName: currentTrack.fileName,
      title: currentTrack.title,
      recognizedTitle: currentTrack.recognizedTitle,
      artist: currentTrack.artist,
      manualTitle: currentTrack.manualTitle,
      manualArtist: currentTrack.manualArtist,
      durationMs: currentTrack.durationMs,
    };

    if (!existingTrack) {
      session.tracks.push(trackSnapshot);
      persistSession();
      return;
    }

    if (
      existingTrack.fileName !== trackSnapshot.fileName ||
      existingTrack.title !== trackSnapshot.title ||
      existingTrack.recognizedTitle !== trackSnapshot.recognizedTitle ||
      existingTrack.artist !== trackSnapshot.artist ||
      existingTrack.manualTitle !== trackSnapshot.manualTitle ||
      existingTrack.manualArtist !== trackSnapshot.manualArtist ||
      existingTrack.durationMs !== trackSnapshot.durationMs
    ) {
      Object.assign(existingTrack, trackSnapshot);
      persistSession();
    }
  }, [currentTrack, isPlaying, persistSession, positionMs]);

  useEffect(() => {
    const session = sessionRef.current;
    if (!session) return;

    let changed = false;
    const nextStoredTracks = session.tracks.map((storedTrack) => {
      const liveTrack = tracks.find((track) => track.id === storedTrack.id);
      if (!liveTrack) return storedTrack;
      if (
        storedTrack.manualTitle === liveTrack.manualTitle &&
        storedTrack.manualArtist === liveTrack.manualArtist
      ) {
        return storedTrack;
      }

      changed = true;
      return {
        ...storedTrack,
        manualTitle: liveTrack.manualTitle,
        manualArtist: liveTrack.manualArtist,
      };
    });

    if (changed) {
      sessionRef.current = {
        ...session,
        tracks: nextStoredTracks,
      };
      persistSession();
    }
  }, [persistSession, tracks]);

  useEffect(() => {
    if (sessionRef.current) return;

    const session = createLocalMp3TestSession({ baseline, preSurvey });
    sessionRef.current = session;
    const initialStorageError = saveLocalMp3TestSession(session);
    lastStorageFlushAtRef.current = Date.now();

    if (initialStorageError) {
      const errorTimer = window.setTimeout(() => {
        setStorageError(initialStorageError);
      }, 0);
      return () => {
        window.clearTimeout(errorTimer);
      };
    }
  }, [baseline, preSurvey]);

  useSensorStream(
    (line) => {
      const packet = parseSensorPacket(line);
      if (!packet) return;

      const signal = createGsrSignal({
        rawAdc: packet.gsrRaw,
        smoothAdc: packet.gsrSmooth ?? packet.gsrDisplay,
      });
      if (signal.rawAdc == null && signal.smoothAdc == null) return;

      const session = sessionRef.current;
      if (!session) return;

      if (
        session.samples.length >= LOCAL_MP3_TEST_SESSION_MAX_SAMPLES
      ) {
        session.samples.splice(0, SAMPLE_TRIM_COUNT);
        session.droppedSampleCount += SAMPLE_TRIM_COUNT;
      }

      const timestamp = Date.now();
      const playback = playbackContextRef.current;
      session.samples.push({
        timestamp,
        elapsedMs: Math.max(0, timestamp - session.startedAt),
        gsrRawAdc: signal.rawAdc,
        gsrSmoothAdc: signal.smoothAdc,
        gsrUiLevel: signal.uiLevel,
        trackId: playback.currentTrack?.id ?? null,
        trackPositionMs: playback.currentTrack
          ? Math.max(0, playback.positionMs)
          : null,
        isPlaying: playback.isPlaying,
      });
      session.lastSampleAt = timestamp;

      if (
        timestamp - lastStorageFlushAtRef.current >=
        STORAGE_FLUSH_INTERVAL_MS
      ) {
        persistSession();
      }
    },
    { trackLastSample: false },
  );

  useEffect(() => {
    const flushForBackground = () => {
      persistSession();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushForBackground();
      }
    };

    window.addEventListener("pagehide", flushForBackground);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", flushForBackground);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      persistSession();
    };
  }, [persistSession]);

  const finishSession = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;

    session.finishedAt = Date.now();
    persistSession();
  }, [persistSession]);

  return {
    finishSession,
    storageError,
  };
}
